import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExploitBlockRuleWithStatus } from '../../types/exploit-rules';
import { PatternEditModal } from './PatternEditModal';

interface HostRuleRowProps {
  rule: ExploitBlockRuleWithStatus;
  onToggle: () => void;
  isPending: boolean;
  formatDate: (dateStr: string) => string;
}

export function HostRuleRow({ rule, onToggle, isPending, formatDate }: HostRuleRowProps) {
  const { t } = useTranslation('exploitRules');
  const [showDetails, setShowDetails] = useState(false);
  const [showPatternEdit, setShowPatternEdit] = useState(false);

  const isDisabled = rule.globally_disabled || rule.host_disabled;
  const hasDetails = rule.exclusion || rule.global_exclusion || true; // Always allow expansion to view pattern

  // Get translated rule name and description
  const ruleName = t(`rules.${rule.name}.name`, { defaultValue: rule.name });
  const ruleDescription = t(`rules.${rule.name}.description`, { defaultValue: rule.description });

  return (
    <div className={`${rule.globally_disabled ? 'bg-amber-50 dark:bg-amber-900/10' : rule.host_disabled ? 'bg-orange-50 dark:bg-orange-900/10' : 'bg-white dark:bg-slate-800'}`}>
      <div
        className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
        onClick={() => hasDetails && setShowDetails(!showDetails)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span
            className={`px-2 py-0.5 rounded text-xs font-mono ${
              rule.globally_disabled
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                : rule.host_disabled
                ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
            }`}
          >
            {ruleName}
          </span>
          <span className="text-sm text-gray-700 dark:text-slate-300 truncate" title={ruleDescription}>
            {ruleDescription || t('policyManager.noDescription')}
          </span>
          <span className="px-1.5 py-0.5 text-xs rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400">
            {rule.pattern_type}
          </span>
          {rule.globally_disabled && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('global.badge')}
            </span>
          )}
          {hasDetails && (
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${showDetails ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
        {rule.globally_disabled ? (
          <span className="text-xs text-amber-600 dark:text-amber-400 italic px-3">
            {t('global.badge')}
          </span>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            disabled={isPending}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${!isDisabled ? 'bg-green-500' : 'bg-gray-300'
            } ${isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${!isDisabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
        )}
      </div>

      {/* Expanded Details Panel */}
      {showDetails && (
        <div className="px-4 pb-4 space-y-3">
          {/* Pattern Details */}
          <div className="ml-4 mr-4 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-700 dark:text-slate-300 flex items-center gap-2">
                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                {t('policyManager.pattern')}
              </h4>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPatternEdit(true);
                }}
                className="px-3 py-1.5 text-xs rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors flex items-center gap-1"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {t('policyManager.editPattern')}
              </button>
            </div>

            {/* Pattern Code Block */}
            <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 overflow-x-auto">
              <code className="text-sm font-mono text-slate-800 dark:text-slate-200 break-all whitespace-pre-wrap">
                {rule.pattern}
              </code>
            </div>

            {/* Pattern Metadata */}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-gray-500 dark:text-slate-500">{t('policyManager.patternType')}</span>
                <p className="font-medium text-gray-700 dark:text-slate-300 mt-0.5">
                  {t(`patternEdit.types.${rule.pattern_type}`, { defaultValue: rule.pattern_type })}
                </p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-slate-500">{t('policyManager.severity')}</span>
                <p className="mt-0.5">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    rule.severity === 'critical'
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                      : rule.severity === 'warning'
                      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                  }`}>
                    {rule.severity}
                  </span>
                </p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-slate-500">
                  {rule.is_system ? t('policyManager.systemRule') : t('policyManager.customRule')}
                </span>
                <p className="font-medium text-gray-700 dark:text-slate-300 mt-0.5">
                  {rule.is_system ? 'System' : 'Custom'}
                </p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-slate-500">Sort Order</span>
                <p className="font-medium text-gray-700 dark:text-slate-300 mt-0.5">
                  {rule.sort_order}
                </p>
              </div>
            </div>
          </div>

          {/* Host Exclusion Details Panel */}
          {rule.exclusion && !rule.globally_disabled && (
            <div className="ml-4 mr-4 bg-orange-100 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-900/40 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold text-orange-800 dark:text-orange-300">{t('policyManager.exclusionInfo')}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-orange-900 dark:text-orange-200">
                <div>
                  <span className="text-orange-600 dark:text-orange-400 text-xs">{t('policyManager.disabledAt')}</span>
                  <p className="font-medium">{formatDate(rule.exclusion.created_at)}</p>
                </div>
                {rule.exclusion.reason && (
                  <div>
                    <span className="text-orange-600 dark:text-orange-400 text-xs">{t('policyManager.reason')}</span>
                    <p className="font-medium">{rule.exclusion.reason}</p>
                  </div>
                )}
                {rule.exclusion.disabled_by && (
                  <div>
                    <span className="text-orange-600 dark:text-orange-400 text-xs">{t('policyManager.disabledBy')}</span>
                    <p className="font-medium">{rule.exclusion.disabled_by}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Global Exclusion Details Panel */}
          {rule.global_exclusion && (
            <div className="ml-4 mr-4 bg-amber-100 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 rounded-lg p-3 text-sm">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-semibold text-amber-800 dark:text-amber-300">{t('global.exclusionInfo')}</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-amber-900 dark:text-amber-200">
                <div>
                  <span className="text-amber-600 dark:text-amber-400 text-xs">{t('policyManager.disabledAt')}</span>
                  <p className="font-medium">{formatDate(rule.global_exclusion.created_at)}</p>
                </div>
                {rule.global_exclusion.reason && (
                  <div>
                    <span className="text-amber-600 dark:text-amber-400 text-xs">{t('policyManager.reason')}</span>
                    <p className="font-medium">{rule.global_exclusion.reason}</p>
                  </div>
                )}
                {rule.global_exclusion.disabled_by && (
                  <div>
                    <span className="text-amber-600 dark:text-amber-400 text-xs">{t('policyManager.disabledBy')}</span>
                    <p className="font-medium">{rule.global_exclusion.disabled_by}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pattern Edit Modal */}
      {showPatternEdit && (
        <PatternEditModal
          rule={rule}
          onClose={() => setShowPatternEdit(false)}
        />
      )}
    </div>
  );
}
